import store from '../store';
import { addAction, removeAction } from './titleBar.action';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import guid from '@whiz-cart/node-shared/guid';
import memoize from '@whiz-cart/node-shared/memoize';
import type { LocationTarget } from '@whiz-cart/ui-shared/url/url.service';
import equals from 'fast-deep-equal/es6/react';
import { MouseEventHandler, ReactElement, useEffect, useRef, useState, type HTMLProps } from 'react';

export type ActionConfig<
    TAction extends string | LocationTarget | MouseEventHandler<HTMLDivElement> =
        | string
        | LocationTarget
        | MouseEventHandler<HTMLDivElement>,
> = {
    index?: number;
    label?: React.ReactNode;
    id: string;
    display?: boolean;
    disabled?: boolean | React.ReactNode;
    icon?: ReactElement | IconProp;
    titleBarLinkId?: string;
    action: TAction;
    actionProps?: HTMLProps<HTMLDivElement> & HTMLProps<HTMLAnchorElement>;
    className?: string;
    isLoading?: boolean;
    userGuid?: string;
};

export default (config: any) => (target: any, key: any, descriptor: any) => {
    const configFn = config instanceof Function ? config : () => config;

    const { componentDidMount, componentDidUpdate, componentWillUnmount } = target;
    const id = `${target.constructor.name}/${key}`;

    const update = (self: any) => {
        dispatch(self, configFn(self.props, self.state));
    };
    const dispatch = memoize((self, config) => {
        if (!('display' in config) || config.display) store.dispatch(addAction({ ...config, action: descriptor.value.bind(self), id }));
        else store.dispatch(removeAction(id));
    });

    target.componentDidMount = function () {
        componentDidMount?.bind(this)();
        update(this);
    };

    target.componentDidUpdate = function (...args: any[]) {
        componentDidUpdate?.bind(this)(...args);
        update(this);
    };

    target.componentWillUnmount = function () {
        componentWillUnmount?.bind(this)();
        store.dispatch(removeAction(id));
    };

    for (const action of store.getState().titleBar.actions as any) {
        const [c, f] = action.id.split('/');
        if (c === target.constructor.name && !target[f]) store.dispatch(removeAction(action.id));
    }
};

export function useAction<TAction extends string | LocationTarget | MouseEventHandler<HTMLDivElement>>(
    config: Omit<ActionConfig<TAction>, 'action'>,
    action: ActionConfig<TAction>['action'] = ((_e) => undefined) as TAction,
    deps: React.DependencyList = [action],
): TAction {
    const [id] = useState(config.id ?? guid());
    const configRef = useRef(config);

    useEffect(() => {
        configRef.current = config;

        if (!('display' in config) || config.display) {
            store.dispatch(addAction({ ...config, action, id }));
        } else store.dispatch(removeAction(id));

        return () => {
            store.dispatch(removeAction(id));
        };
    }, [equals(config, configRef.current) ? configRef.current : config, ...deps]);

    return action;
}
